/**
 * Side-by-side layout for overlapping appointments within a single calendar
 * column (one day / one provider / one room).
 *
 * Flutter parity: `dateOverlap()` (calendar_event_card_widget.dart:77) offsets
 * overlapping appointments so they're distinguishable. Flutter uses a crude
 * fixed left-offset cascade (0 / 40 / 80px, capped at 3) and does NOT shrink
 * widths; this React version is a fuller column-partition that also divides
 * the width. The one semantic we DO match exactly: Flutter counts an overlap
 * with `data.start.isBefore(other.end)` (strict `<`), so an event starting
 * exactly when another ends is NOT an overlap — touching intervals are
 * non-overlapping in both (see the `>=` boundary checks below). (PROD-982.)
 *
 * The algorithm — a standard interval-partition:
 *
 *   1. Sort by start (then end).
 *   2. Group into CLUSTERS of transitively-overlapping events (a new cluster
 *      begins when an event starts at/after every prior event in the current
 *      cluster has ended).
 *   3. Within a cluster, greedily assign each event to the first column whose
 *      last event has already ended; otherwise open a new column.
 *   4. Every event in a cluster shares the cluster's column count, so the
 *      widths line up (each = 100 / columnCount %).
 *
 * Touching intervals (one ends exactly when the next starts) are treated as
 * NON-overlapping — they reuse the same column / start a new cluster.
 */

export interface OverlapItem {
  /** Minute-of-day start (e.g. 9:30 → 570). */
  startMin: number;
  /** Minute-of-day end (exclusive). */
  endMin: number;
}

export interface OverlapSlot {
  /** 0-based column this item occupies within its overlap cluster. */
  columnIndex: number;
  /** Total columns in the cluster — drives the width fraction. */
  columnCount: number;
}

/**
 * Returns a Map keyed by the SAME object references passed in, so callers can
 * look up each appointment's slot without re-deriving identity. Items with a
 * non-finite or inverted range still get a 1×1 slot (full width) so a bad row
 * never disappears.
 */
export function computeOverlapLayout<T extends OverlapItem>(
  items: readonly T[],
): Map<T, OverlapSlot> {
  const result = new Map<T, OverlapSlot>();
  if (items.length === 0) return result;

  const sorted = [...items].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin,
  );

  let cluster: T[] = [];
  let clusterEnd = Number.NEGATIVE_INFINITY;

  const flush = () => {
    if (cluster.length === 0) return;
    // `colEnds[c]` = end-min of the last event placed in column c.
    const colEnds: number[] = [];
    const colOf = new Map<T, number>();
    for (const ev of cluster) {
      let placed = -1;
      for (let c = 0; c < colEnds.length; c++) {
        if (ev.startMin >= colEnds[c]) {
          placed = c;
          break;
        }
      }
      if (placed === -1) {
        placed = colEnds.length;
        colEnds.push(ev.endMin);
      } else {
        colEnds[placed] = ev.endMin;
      }
      colOf.set(ev, placed);
    }
    // `flush` runs only AFTER the whole cluster has been accumulated, so
    // `columnCount` is the cluster-wide FINAL maximum — it's stamped on every
    // member in this single pass. An event committed to an early column still
    // gets the final (possibly larger) count once later events widen the
    // cluster; there is no stale per-event lock-in (Samer review §1).
    const columnCount = colEnds.length;
    for (const ev of cluster) {
      result.set(ev, { columnIndex: colOf.get(ev) ?? 0, columnCount });
    }
    cluster = [];
  };

  for (const ev of sorted) {
    if (cluster.length > 0 && ev.startMin >= clusterEnd) {
      flush();
      clusterEnd = Number.NEGATIVE_INFINITY;
    }
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.endMin);
  }
  flush();

  return result;
}

/** Convenience: percentage geometry from a slot. */
export function slotToPercents(slot: OverlapSlot): {
  leftPct: number;
  widthPct: number;
} {
  const widthPct = 100 / Math.max(1, slot.columnCount);
  return { leftPct: slot.columnIndex * widthPct, widthPct };
}
